import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { gsApi } from "../auth/auth";
import {
  organizations,
  accessGroups,
  accessGroupMembers,
  accessMembers,
} from "../app/api";
import {
  getOpts,
  getResponse,
  getSerializedData,
} from "../helpers/JSONapi";
import { defaultNotificationSettings, fileDownloadThunkResponse, getThunkResponse, saveThunkResponse, thunkResponse } from "../app/apiv2";

//get
export const getOrganizationAccessLists = createAsyncThunk(
  "access/getOrganizationAccessLists",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${organizations}/${id}/access-groups`);
    return response;
  }
);

export const getOrganizationAccessList = createAsyncThunk(
  "access/getOrganizationAccessList",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${accessGroups}/${id}`);
    return response;
  }
);

export const getOrganizationAccessListCodes = createAsyncThunk(
  "access/getOrganizationAccessListCodes",
  async (filter, thunkAPI) => {
    const f = {
      per_page: filter.perPage,
      page: filter.page,
      search_text: filter.searchText,
      include: "access-member",
    };
    const response = await gsApi.get(
      `${accessGroups}/${filter?.id}/access-group-members`,
      { data: f }
    );
    return response;
  }
);

export const getOrganizationAccessListCode = createAsyncThunk(
  "access/getOrganizationAccessListCode",
  async (id, thunkAPI) => {
    const response = await gsApi.get(`${accessGroupMembers}/${id}`);
    return response;
  }
);

export const getOrganizationAccessListCodeMember = createAsyncThunk(
  "access/getOrganizationAccessListCodeMember",
  async (id, thunkAPI) => {
    const response = await gsApi.get(
      `${accessGroupMembers}/${id}/access-member`
    );
    return response;
  }
);

//save
export const saveAccessList = createAsyncThunk(
  "access/saveAccessList",
  async (accessList, thunkAPI) => {
    return await saveAccessGroup(accessList, thunkAPI);
  }
);

export const saveAccessGroup = async (accessList, thunkAPI) => {
  const serializedData = getSerializedData(
    accessList,
    "access-groups",
    getOpts(accessList, getOrganizationRelationships(accessList))
  );
  return await getResponse(
    gsApi.patch,
    `${accessGroups}/${accessList.id}`,
    serializedData,
    thunkAPI
  );
};

const saveAccessMember = async (member, thunkAPI) => {
  const serializedData = getSerializedData(
    member,
    "access-members",
    getOpts(member)
  );
  return await getResponse(
    gsApi.patch,
    `${accessMembers}/${member.id}`,
    serializedData,
    thunkAPI
  );
};

export const saveAccessCode = createAsyncThunk(
  "access/saveAccessCode",
  async (accessCode, thunkAPI) => {
    if (accessCode.accessMember) {
      let memberResponse = await saveAccessMember(
        accessCode.accessMember,
        thunkAPI
      );
      if (memberResponse) {
        const serializedData = getSerializedData(
          accessCode,
          "access-group-members",
          getOpts(accessCode, [
            ...getMemberRelationships(accessCode),
            ...getGroupRelationships(),
          ])
        );
        return await getResponse(
          gsApi.patch,
          `${accessGroupMembers}/${accessCode.id}`,
          serializedData,
          thunkAPI
        );
      } else {
        return memberResponse;
      }
    }
  }
);

//validate
export const validateOrganizationAccessList = createAsyncThunk(
  "access/validateOrganizationAccessList",
  async (verify, thunkAPI) => {
    const response = await getResponse(
      gsApi.post,
      `${accessGroups}/${verify?.id}/validate-access-group-members`,
      verify,
      thunkAPI
    );
    return response;
  }
);

//import
export const importOrganizationAccessList = createAsyncThunk(
  "access/importOrganizationAccessList",
  async (verify, thunkAPI) => {
    const response = await getResponse(
      gsApi.post,
      `${accessGroups}/${verify?.id}/import`,
      verify,
      thunkAPI
    );
    return response;
  }
);

//export

export const downloadOrganizationAccessList = createAsyncThunk(
  "access/downloadOrganizationAccessList",
  async (id, thunkAPI) => {
    const response = await gsApi.get(
      `${accessGroups}/${id}/access-group-export`,
      { responseType: "arraybuffer" }
    );
    return response;
  }
);

//create
export const createAccessList = createAsyncThunk(
  "access/createAccessList",
  async (accessList, thunkAPI) => {
    const serializedData = getSerializedData(
      accessList,
      "access-groups",
      getOpts(accessList, getOrganizationRelationships(accessList))
    );
    return await getResponse(
      gsApi.post,
      `${accessGroups}`,
      serializedData,
      thunkAPI
    );
  }
);

const accessMemberEmailSearch = async (accessCode, thunkAPI) => {
  return await getResponse(
    gsApi.get,
    `${organizations}/${accessCode?.organization?.id}/access-member-search`,
    { data: { access_member_email: accessCode?.accessMember?.email } },
    thunkAPI
  );
};

const createAccessMember = async (member, thunkAPI) => {
  const serializedData = getSerializedData(
    member,
    "access-members",
    getOpts(member)
  );
  return await getResponse(
    gsApi.post,
    `${accessMembers}`,
    serializedData,
    thunkAPI
  );
};

export const createAccessCode = createAsyncThunk(
  "access/createAccessCode",
  async (code, thunkAPI) => {
    let accessCode = {...code}
    if (accessCode.accessMember) {
      let searchResponse = await accessMemberEmailSearch(accessCode);
      if (searchResponse?.id) {
        accessCode.accessMember = searchResponse;
      } else {
        let memberResponse = await createAccessMember(accessCode.accessMember);
        accessCode.accessMember = memberResponse;
      }
      const serializedData = getSerializedData(
        accessCode,
        "access-group-members",
        getOpts(accessCode, [
          ...getMemberRelationships(),
          ...getGroupRelationships(),
          ...getOrganizationRelationships(),
        ])
      );
      return await getResponse(
        gsApi.post,
        `${accessGroupMembers}`,
        serializedData,
        thunkAPI
      );
    }
  }
);

//delete

export const deleteOrganizationAccessList = createAsyncThunk(
  "access/deleteOrganizationAccessList",
  async (list, thunkAPI) => {
    const deleteThis = { ...list, status: "deleted" };
    return await saveAccessGroup(deleteThis, thunkAPI);
  }
);

export const deleteOrganizationAccessCode = createAsyncThunk(
  "access/deleteOrganizationAccessCode",
  async (id, thunkAPI) => {
    const response = await gsApi.delete(`${accessGroupMembers}/${id}`, {
      data: {},
    });
    return response;
  }
);

export const accessSlice = createSlice({
  name: "access",
  initialState: {
    organizationAccessLists: [],
    selectedAccessList: {},
    selectedAccessListCodes: [],
    selectedAccessCode: {},
    selectedAccessCodeMember: {},
    validatedAccessCodeMember: {},
    slideOutNotifications: [],
    notifications: [],
    loading: [],
  },
  reducers: {
    setOrganizationAccessLists: (state, action) => {
      state.organizationAccessLists = action.payload;
    },
    setSelectedList: (state, action) => {
      state.selectedAccessList = action.payload;
    },
    setSelectedCode: (state, action) => {
      state.selectedAccessCode = action.payload;
    },
    setValidatedMember: (state, action) => {
      state.validatedAccessCodeMember = action.payload;
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
  },
  extraReducers: (builder) => {
    getThunkResponse(builder, getOrganizationAccessLists, (state, action) => {state.organizationAccessLists = action.payload;})
    getThunkResponse(builder,getOrganizationAccessList, (state, action) => {
      state.selectedAccessList = action.payload;
      const index = state.organizationAccessLists?.findIndex?.(
        (l) => l.id === action.payload.id
      );
      state.organizationAccessLists[index] = {
        ...action.payload,
      };
    })
    getThunkResponse(builder,getOrganizationAccessListCodes, (state, action) => {state.selectedAccessListCodes = action.payload;})
    getThunkResponse(builder, getOrganizationAccessListCode, (state, action) => {state.selectedAccessCode = action.payload;})
    fileDownloadThunkResponse(builder,downloadOrganizationAccessList, (state, action) => `${state.selectedAccessList.name}-access-list.xlsx`)
    getThunkResponse(builder, getOrganizationAccessListCodeMember, (state, action) => {state.selectedAccessCodeMember = action.payload;})
    saveThunkResponse(builder, saveAccessList, (state, action) => {
      state.selectedAccessList = action.payload;
      const index = state.organizationAccessLists?.findIndex?.(
        (l) => l.id === action.payload.id
      );
      state.organizationAccessLists[index] = {
        ...action.payload,
      };
    }, () => "Saving access list", () => "Access List Saved")
    saveThunkResponse(builder, saveAccessCode, (state, action) => {
      state.selectedAccessCode = action.payload;
      const index = state.selectedAccessListCodes?.findIndex?.(
        (l) => l.id === action.payload.id
      );
      state.selectedAccessListCodes[index] = {
        ...action.payload,
      };
    }, () => "Saving access codes", () => "Access codes Saved")
    thunkResponse(builder,validateOrganizationAccessList, (state, action) => {
      const member = action?.payload?.data;
      state.validatedAccessCodeMember = {
        activateOn: member.activate_on,
        deactivateOn: member.deactivate_on,
        maxUseQuantity: member.max_use_quantity,
        identifier: member.identifier,
        accessMember: {
          firstName: member.first_name,
          lastName: member.last_name,
          email: member.email,
          ghinNumber: member.ghin_number,
        },
      };
    }, {...defaultNotificationSettings, pending: {...defaultNotificationSettings.pending, message: () => "Validating Access List"}, fulfilled: {...defaultNotificationSettings.fulfilled, message: () => "Access list has been validated!"}})
    thunkResponse(builder,importOrganizationAccessList, (state, action) => {}, {...defaultNotificationSettings, pending: {...defaultNotificationSettings.pending, message: () => "Importing Access List"}, fulfilled: {...defaultNotificationSettings.fulfilled, message: () => "Access list has been imported"}})
    saveThunkResponse(builder,createAccessList, (state, action) => {
      state.selectedAccessList = action.payload;
      state.organizationAccessLists = [
        action.payload,
        ...state.organizationAccessLists,
      ];
    }, () => "Creating Access List", () => "Access List Created")
    saveThunkResponse(builder, createAccessCode, (state, action) => {
      state.selectedAccessCode = action.payload;
      state.selectedAccessListCodes = [
        action.payload,
        ...state.selectedAccessListCodes,
      ];
    }, () => "Creating Access Code", () => "Access Code has been created.")
    saveThunkResponse(builder, deleteOrganizationAccessList, (state, action) => {
      const delID = action?.meta?.arg?.id
        ? action?.meta?.arg?.id
        : action?.meta?.arg;
      const filteredList = state.organizationAccessLists?.filter?.(
        (l) => l.id !== delID
      );
      state.organizationAccessLists = filteredList;
    }, () => "Deleting Access List", () => "Access List Deleted")
    saveThunkResponse(builder, deleteOrganizationAccessCode, (state, action) => {
      const filteredList = state.selectedAccessListCodes?.filter?.(
        (l) => l.id !== action.meta.arg
      );
      state.selectedAccessListCodes = filteredList;
    }, ()=>"Deleting Access Code", () => "Access Code has been deleted")
  }
});

export const {
  setOrganizationAccessLists,
  setSelectedList,
  clearNotifications,
  setSelectedCode,
  setValidatedMember,
} = accessSlice.actions;

export default accessSlice.reducer;

export const selectOrganizationAccessLists = (state) =>
  state.access.organizationAccessLists;

export const selectCurrentList = (state) => state.access.selectedAccessList;

export const selectLoading = (state) => state.access.loading;

export const selectAccessListNotifications = (state) =>
  state.access.notifications;

export const selectCurrentListCodes = (state) =>
  state.access.selectedAccessListCodes;

export const selectCurrentAccessCode = (state) =>
  state.access.selectedAccessCode;

export const selectCurrentAccessCodeMember = (state) =>
  state.access.selectedAccessCodeMember;

export const selectValidatedMember = (state) =>
  state.access.validatedAccessCodeMember;

function getOrganizationRelationships() {
  const relationships = [];
  relationships.push({
    name: "organization",
    opt: { ref: "id", included: true },
  });
  return relationships;
}

function getMemberRelationships() {
  const relationships = [];
  relationships.push({
    name: "accessMember",
    opt: { ref: "id", included: true },
  });
  return relationships;
}

function getGroupRelationships() {
  const relationships = [];
  relationships.push({
    name: "accessGroup",
    opt: { ref: "id", included: true },
  });
  return relationships;
}
